import axios from "axios";
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from "../auth/tokenStorage";

// The token endpoints live under gf/urls.py (shared with pharmacy staff
// JWT auth), NOT under /clinic/ — doctors log in via the same
// GFTokenObtainPairView pharmacy Employees use, since Doctor.user is a
// real Django User. Everything else clinic-specific lives under /clinic/.
const GF_BASE_URL = process.env.REACT_APP_GF_BASE_URL || "http://localhost:8000/gf/";
const CLINIC_BASE_URL = process.env.REACT_APP_CLINIC_BASE_URL || "http://localhost:8000/clinic/";

export const gfApi = axios.create({ baseURL: GF_BASE_URL });

// Logo uploads (see gf.views.upload_org_logo) return a RELATIVE path
// (e.g. "/media/org-logos/<id>.png"), not an absolute URL — correct in
// dev where frontend and backend share an origin, but not necessarily in
// production if the frontend is served from a different origin than the
// API. Prefixing with the API's own origin keeps <img src> working
// either way.
export const MEDIA_BASE_URL = new URL(GF_BASE_URL).origin;

const api = axios.create({ baseURL: CLINIC_BASE_URL });

function attachAuthHeaders(config) {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Only fills in the currently-selected clinic when the caller hasn't
  // already set this header itself — e.g. DoctorProfilePage.js's "leave
  // clinic" action targets one specific row from the My Clinics list,
  // which may not be whichever clinic is currently selected.
  if (!config.headers["X-Organization-Id"]) {
    const orgId = localStorage.getItem("clinic_selected_org_id");
    if (orgId) config.headers["X-Organization-Id"] = orgId;
  }

  return config;
}

api.interceptors.request.use(attachAuthHeaders);
// gfApi originally only ever carried the unauthenticated token/refresh/
// call, so it never needed this — now that it's also used for
// IsAuthenticated gf endpoints (e.g. orgsettings/upload-logo/, see
// AdminSettings.js), it needs the same Bearer token attached or every
// such call 401s with "Authentication credentials were not provided."
gfApi.interceptors.request.use(attachAuthHeaders);

// ── Refresh-on-401, with a single in-flight refresh shared across any
// requests that fail at the same time (rather than each one kicking
// off its own refresh call and racing to rotate the same token) ──────
let refreshPromise = null;

function doRefresh() {
  if (!refreshPromise) {
    const refresh = getRefreshToken();
    if (!refresh) {
      refreshPromise = Promise.reject(new Error("No refresh token available"));
    } else {
      refreshPromise = gfApi
        .post("token/refresh/", { refresh })
        .then((res) => {
          // ROTATE_REFRESH_TOKENS=True on the backend — the response
          // includes a new refresh token; the old one is now blacklisted.
          setTokens(res.data.access, res.data.refresh);
          return res.data.access;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }
  }
  return refreshPromise;
}

// Shared refresh-on-401 logic for BOTH clients — gfApi carries just as
// many IsAuthenticated calls as `api` now (orgsettings, upload-logo,
// test-sms; see attachAuthHeaders' comment above), so a gfApi call made
// after the 15-minute access token expires must refresh exactly like an
// `api` call does, not fail outright with a raw 401 that looks like a
// downstream error (e.g. surfacing as "SMS send failed" on the Settings
// page when it's really just a stale token). `client` is whichever
// instance the failing request came from, so the retry replays on that
// same instance rather than hardcoding one.
function attachRefreshOn401(client) {
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config, response } = error;
      const isAuthEndpoint = config?.url?.includes("token/");

      if (response?.status === 401 && !config._retry && !isAuthEndpoint) {
        config._retry = true;
        try {
          const newAccess = await doRefresh();
          config.headers.Authorization = `Bearer ${newAccess}`;
          return client(config);
        } catch (refreshErr) {
          clearTokens();
          localStorage.removeItem("clinic_selected_org_id");
          localStorage.removeItem("clinic_selected_org_name");
          // Interceptors run outside React Router — a hard redirect is
          // the simplest correct way to force back to /login from here.
          window.location.href = "/login";
          return Promise.reject(refreshErr);
        }
      }

      return Promise.reject(error);
    }
  );
}

attachRefreshOn401(api);
attachRefreshOn401(gfApi);

export default api;